import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Globe, MapPin } from 'lucide-react';
import { useT } from '../i18n';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const GeoMap = ({ geoData }) => {
  const { t, locale } = useT();
  const [mapCenter, setMapCenter] = useState([20, 0]);
  const [zoom, setZoom] = useState(2);

  useEffect(() => {
    if (geoData && geoData.map_data && geoData.map_data.length > 0) {
      const validPoints = geoData.map_data.filter(
        (point) => point.latitude !== 0 && point.longitude !== 0
      );

      if (validPoints.length > 0) {
        const avgLat = validPoints.reduce((sum, p) => sum + p.latitude, 0) / validPoints.length;
        const avgLng = validPoints.reduce((sum, p) => sum + p.longitude, 0) / validPoints.length;
        setMapCenter([avgLat, avgLng]);
        setZoom(4);
      }
    }
  }, [geoData]);

  if (!geoData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Globe className="w-5 h-5" />
            <span>{t('Geographic statistics')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('Loading geographic data...')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const validMapData = geoData.map_data?.filter(
    (point) => point.latitude !== 0 && point.longitude !== 0
  ) || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="w-5 h-5" />
              <span>{t('By country')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {geoData.country_stats && geoData.country_stats.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {geoData.country_stats.map((country, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="text-xs">
                        {country.country_code}
                      </Badge>
                      <span className="text-sm font-medium">{country.country}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{t('{n} clicks', { n: country.count })}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">{t('No country data')}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="w-5 h-5" />
              <span>{t('By city')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {geoData.city_stats && geoData.city_stats.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {geoData.city_stats.map((city, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium">{city.city}</span>
                      <p className="text-xs text-muted-foreground">{city.country}</p>
                    </div>
                    <span className="text-sm text-muted-foreground">{t('{n} clicks', { n: city.count })}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">{t('No city data')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MapPin className="w-5 h-5" />
            <span>{t('Click map')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {validMapData.length > 0 ? (
            <div className="h-96 w-full rounded-lg overflow-hidden">
              <MapContainer
                center={mapCenter}
                zoom={zoom}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {validMapData.map((point, index) => (
                  <Marker key={index} position={[point.latitude, point.longitude]}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-medium">{point.city}, {point.country}</div>
                        <div className="text-muted-foreground">
                          {new Date(point.timestamp).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {(Number(point.latitude) || 0).toFixed(4)}, {(Number(point.longitude) || 0).toFixed(4)}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          ) : (
            <div className="text-center py-16">
              <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t('No data to display on map')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('Geographic data will appear after the first clicks with resolvable coordinates')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GeoMap;
